import type { ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import type { ListPageTab } from "_shared/_views/list-pages/list-page-heading";
import { useCurrentTableView } from "_shared/_views/list-pages/use-current-table-view";
import {
  readTeamsChips,
  TEAMS_CURRENT_VIEW,
  teamsTabPath,
  writeTeamsChips,
} from "_shared/teams/teams-list-chips";
import { PageTab } from "_shared/teams/teams-tabs";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import Session from "users/__session/session";
import { TeamTemplateFilterParameters } from "work/team-templates/_api/queries/team-template-query";
import { useTeamTemplateListCounts } from "work/team-templates/_api/team-template-api";
import { useActiveTeamCounts } from "work/work-agreements/teams/_api/teams-api-service";

/**
 * The URL state both tabs of the Teams page share (#6718): the chips, the reader's changes kept as their
 * view (#6714), and moving between tabs. Each tab reads its own search, sort and page from the same URL.
 */
export function useTeamsListUrl(tab: PageTab) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // A plain visit to `/teams` opens the reader's starred view or the one they left, else Teams.
  const { remember } = useCurrentTableView(TEAMS_CURRENT_VIEW);

  // Memoised on the URL, because the list's request is built from them.
  const searchKey = searchParams.toString();
  const chips = useMemo(() => readTeamsChips(tab, new URLSearchParams(searchKey)), [searchKey, tab]);

  /** Shows `next` and keeps it as the reader's view: every change the reader makes goes through here. */
  const showReaderChange = useCallback(
    (next: URLSearchParams) => {
      if (next.toString() === searchParams.toString()) return;
      remember(`${location.pathname}?${next.toString()}`);
      setSearchParams(next);
    },
    [location.pathname, remember, searchParams, setSearchParams],
  );

  const onTabChange = useCallback(
    (next: PageTab) => {
      // A tab is a different kind of thing, so nothing the other tab's chips, search or sort said goes with it.
      const path = teamsTabPath(next);
      remember(path);
      void navigate(path);
    },
    [navigate, remember],
  );

  const onChipsChange = useCallback(
    (selection: ListChipSelection) => showReaderChange(writeTeamsChips(tab, selection, searchParams)),
    [searchParams, showReaderChange, tab],
  );

  return { searchParams, searchKey, chips, showReaderChange, onTabChange, onChipsChange };
}

/**
 * What a Templates counts request is narrowed by: the list's criteria, less the archived state the
 * counts split on.
 */
export function teamTemplateCountsCriteria(session: Session, search: string | undefined): string {
  const criteria = TeamTemplateFilterParameters.create({
    creatorEntityId: session.user?.legalEntityId,
    query: search?.trim() ? search.trim() : undefined,
  }).searchParams;
  criteria.delete("isArchived");
  return criteria.toString();
}

/**
 * Both tabs' badges and the tab's chip counts, one counts request per tab (#6709). The tab on screen
 * counts under its own search; the other under none, since its list has none until the reader goes there.
 * A badge does not move with the chips.
 */
export function useTeamsPageTabs(args: Readonly<{ activeTab: PageTab; search: string; session: Session }>) {
  const { activeTab, search, session } = args;
  const teamsSearch = activeTab === PageTab.Teams ? search : "";
  const templatesSearch = activeTab === PageTab.Templates ? search : "";
  const teamCounts = useActiveTeamCounts({ search: teamsSearch.trim() || undefined });
  const templateCriteria = useMemo(
    () => teamTemplateCountsCriteria(session, templatesSearch),
    [session, templatesSearch],
  );
  const templateCounts = useTeamTemplateListCounts({ criteria: templateCriteria });

  const tabs = useMemo<readonly ListPageTab<PageTab>[]>(
    () => [
      {
        value: PageTab.Teams,
        label: "Teams",
        counts: "team(s) on running work",
        count: teamCounts.counts?.total,
        isLoading: teamCounts.isLoading,
        badgeColor: "info",
      },
      {
        value: PageTab.Templates,
        label: "Templates",
        counts: "team template(s)",
        count: templateCounts.counts?.templates,
        isLoading: templateCounts.isLoading,
        badgeColor: "info",
      },
    ],
    [teamCounts.counts?.total, teamCounts.isLoading, templateCounts.counts?.templates, templateCounts.isLoading],
  );

  return { tabs, teamCounts: teamCounts.counts, templateCounts: templateCounts.counts };
}
